import { AmpereUnit } from '@/entities/ampere-unit';
import { PowerUnit } from '@/entities/power-unit';
import { VoltageUnit } from '@/entities/voltage-unit';

export const powerLabels: Record<PowerUnit, string> = {
  [PowerUnit.W]: 'W',
  [PowerUnit.KW]: 'kW',
  [PowerUnit.MW]: 'MW',
  [PowerUnit.GW]: 'GW',
  [PowerUnit.TW]: 'TW',
};

export function formatPower(
  w: number,
  unit: PowerUnit,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(w / 10 ** unit)} ${powerLabels[unit]}`;
}

export const energyLabels: Record<PowerUnit, string> = {
  [PowerUnit.W]: 'Wh',
  [PowerUnit.KW]: 'kWh',
  [PowerUnit.MW]: 'MWh',
  [PowerUnit.GW]: 'GWh',
  [PowerUnit.TW]: 'TWh',
};

export function formatEnergy(
  wh: number,
  unit: PowerUnit,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(wh / 10 ** unit)} ${energyLabels[unit]}`;
}

export const voltageLabels: Record<VoltageUnit, string> = {
  [VoltageUnit.V]: 'V',
  [VoltageUnit.KV]: 'kV',
  [VoltageUnit.MV]: 'MV',
};

export function formatVolt(
  v: number,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(v)} V`;
}

export function formatVoltageWithUnit(
  v: number,
  unit: VoltageUnit,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
): string {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(v / 10 ** unit)} ${voltageLabels[unit]}`;
}

export const ampereLabels: Record<AmpereUnit, string> = {
  [AmpereUnit.A]: 'A',
  [AmpereUnit.KA]: 'kA',
  [AmpereUnit.MA]: 'MA',
};

export function formatAmpere(
  a: number,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(a)} A`;
}

export function formatAmpereWithUnit(
  a: number,
  unit: AmpereUnit,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(a / 10 ** unit)} ${ampereLabels[unit]}`;
}

export function formatAmpereHours(
  ah: number,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(ah)} Ah`;
}

export function formatFrequency(
  hz: number,
  minimumFractionDigits: number = 1,
  maximumFractionDigits: number = minimumFractionDigits,
) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return `${formatter.format(hz)} Hz`;
}

export function formatPowerFactor(
  factor: number,
  minimumFractionDigits: number = 2,
  maximumFractionDigits: number = minimumFractionDigits,
) {
  const formatter = new Intl.NumberFormat('en-US', {
    style: 'decimal',
    minimumFractionDigits,
    maximumFractionDigits,
  });

  return formatter.format(factor);
}
